"use client";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TerminalPanelHeader } from "@/components/ui/terminal-panel";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useThemeConfig } from "@/hooks/use-theme-config";
import { cn } from "@/lib/utils";
import { useAuth } from "@/providers/auth-provider";
import { AnimatePresence, LazyMotion, domAnimation, m } from "framer-motion";
import { ArrowRight, User } from "lucide-react";
import Image from '@/components/ui/image';
import { useRoutePathname } from "@/hooks/use-route-pathname";
import { useState } from "react";

const NOISE_SVG = `data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.05'/%3E%3C/svg%3E`;

// Scanline texture for Bloomberg terminal effect
// Note: SVG uses hardcoded color as data URIs cannot reference CSS variables
// The color matches --terminal-green-primary (#10b981) at 3% opacity
const SCANLINE_SVG = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='2'%3E%3Crect width='100' height='1' fill='rgba(16,185,129,0.03)'/%3E%3Crect y='1' width='100' height='1' fill='transparent'/%3E%3C/svg%3E`;

const openLoginDialog = () => {
	if (typeof window === "undefined") return;
	window.dispatchEvent(
		new CustomEvent("open-login-dialog", {
			detail: {
				description:
					"Unlock full module access and save your place. For free.",
			},
		}),
	);
};

// Terminal-style loading state with blinking cursor (shown when effects are enabled)
function EffectsLoadingState() {
	return (
		<div className="h-64 flex flex-col items-center justify-center gap-3">
			<div className="flex items-center gap-1 font-mono text-[var(--accent-primary)]/80 text-sm">
				<span className="text-[var(--accent-primary)]/50">&gt;</span>
				<span>LOADING DATA</span>
				<span className="inline-block w-2 h-4 bg-[var(--accent-primary)] animate-terminal-cursor" />
			</div>
			<div className="flex gap-1">
				{[0, 1, 2, 3, 4].map((i) => (
					<div
						key={i}
						className="w-1.5 bg-[var(--accent-primary)]/30 rounded-sm animate-terminal-bar"
						style={{ animationDelay: `${i * 0.1}s` }}
					/>
				))}
			</div>
			<span className="text-[10px] font-mono text-[var(--accent-primary)]/40 uppercase tracking-widest">
				Establishing connection...
			</span>
		</div>
	);
}

interface ChartCardProps extends React.HTMLAttributes<HTMLDivElement> {
	title: string;
	subtitle?: string;
	action?: React.ReactNode;
	isLoading?: boolean;
	loadingSkeleton?: React.ReactNode;
	/** Terminal code displayed in header (e.g., "AAPL.RANK" or "TOP.200") */
	terminalCode?: string;
}

// Background overlays/decorations - Bloomberg terminal chrome (conditional on theme config)
function ChartCardDecorations({
	activeHasScanlines,
	activeHasGlow,
	activeHasCRT,
}: {
	activeHasScanlines: boolean;
	activeHasGlow: boolean;
	activeHasCRT: boolean;
}) {
	return (
		<>
			{/* Scanline overlay - CRT monitor effect (conditional on theme config) */}
			{activeHasScanlines && (
				<div
					className="absolute inset-0 pointer-events-none z-[1]"
					style={{
						backgroundImage: `url("${SCANLINE_SVG}")`,
						backgroundRepeat: "repeat",
					}}
				/>
			)}

			{/* Subtle noise texture for depth */}
			<div
				className={cn(
					"absolute inset-0 pointer-events-none z-[2]",
					activeHasScanlines ? "opacity-[0.015]" : "opacity-[0.03]",
				)}
				style={{ backgroundImage: `url("${NOISE_SVG}")` }}
			/>

			{/* Inner gradient glow - only for glow-enabled themes */}
			{activeHasGlow && (
				<div className="absolute inset-0 pointer-events-none z-[3] bg-gradient-to-b from-[var(--accent-primary)]/[0.04] via-transparent to-[var(--accent-primary)]/[0.02]" />
			)}

			{/* Radial vignette for depth - only for CRT themes */}
			{activeHasCRT && (
				<div className="absolute inset-0 pointer-events-none z-[4] bg-[radial-gradient(ellipse_at_center,transparent_0%,transparent_60%,rgba(0,0,0,0.3)_100%)]" />
			)}

			{/* Corner accents - Bloomberg panel style (only for glow themes) */}
			{activeHasGlow && (
				<>
					{/* Top-left corner */}
					<div className="absolute top-0 left-0 w-4 h-4 z-[5]">
						<div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-[var(--accent-primary)]/80 to-transparent" />
						<div className="absolute top-0 left-0 w-[2px] h-full bg-gradient-to-b from-[var(--accent-primary)]/80 to-transparent" />
					</div>
					{/* Top-right corner */}
					<div className="absolute top-0 right-0 w-4 h-4 z-[5]">
						<div className="absolute top-0 right-0 w-full h-[2px] bg-gradient-to-l from-[var(--accent-primary)]/80 to-transparent" />
						<div className="absolute top-0 right-0 w-[2px] h-full bg-gradient-to-b from-[var(--accent-primary)]/80 to-transparent" />
					</div>
					{/* Bottom-left corner */}
					<div className="absolute bottom-0 left-0 w-4 h-4 z-[5]">
						<div className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-[var(--accent-primary)]/80 to-transparent" />
						<div className="absolute bottom-0 left-0 w-[2px] h-full bg-gradient-to-t from-[var(--accent-primary)]/80 to-transparent" />
					</div>
					{/* Bottom-right corner */}
					<div className="absolute bottom-0 right-0 w-4 h-4 z-[5]">
						<div className="absolute bottom-0 right-0 w-full h-[2px] bg-gradient-to-l from-[var(--accent-primary)]/80 to-transparent" />
						<div className="absolute bottom-0 right-0 w-[2px] h-full bg-gradient-to-t from-[var(--accent-primary)]/80 to-transparent" />
					</div>
				</>
			)}
		</>
	);
}

// Card header - terminal vs standard layout
function ChartCardHeader({
	title,
	subtitle,
	action,
	isLoading,
	activeHasGlow,
	activeUseMono,
}: {
	title: string;
	subtitle?: string;
	action?: React.ReactNode;
	isLoading: boolean;
	activeHasGlow: boolean;
	activeUseMono: boolean;
}) {
	return activeHasGlow ? (
		/* Terminal mode: show subtitle tooltip and action only */
		<div className="flex items-center justify-between mb-2">
			<div className="flex items-center gap-2">
				{/* Subtitle with tooltip if provided */}
				{subtitle && (
					<TooltipProvider delayDuration={200}>
						<Tooltip>
							<TooltipTrigger asChild>
								<button
									type="button"
									className={cn(
										"font-mono text-[10px] text-[var(--text-muted)] uppercase tracking-wider",
										"hover:text-[var(--text-secondary)] transition-colors cursor-help",
										"border-b border-dashed border-[var(--card-border)] pb-0.5",
									)}
								>
									INFO
								</button>
							</TooltipTrigger>
							<TooltipContent className="bg-[var(--card-bg)]/95 border-[var(--card-border)] text-[var(--text-primary)] shadow-[var(--border-glow)]">
								<p className="max-w-xs text-xs font-mono tracking-wide">
									{subtitle}
								</p>
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				)}
			</div>
			{/* Action slot */}
			{action && !isLoading && <div>{action}</div>}
		</div>
	) : (
		/* Standard mode: full header with title, subtitle tooltip, action.
		   The header wraps: at 390 px the worldwide-ratings card carries a three-control
		   action row against this page's longest title and clipped its CSV button off the
		   right edge. `min-w-0` lets the title shrink instead of pushing the action out. */
		<div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 mb-2">
			<div className="flex min-w-0 items-center">
				{/* Title */}
				<div>
					{subtitle ? (
						<TooltipProvider delayDuration={200}>
							<Tooltip>
								<TooltipTrigger asChild>
									<button
										type="button"
										className={cn(
											"leading-none cursor-help border-b border-dashed pb-0.5 bg-transparent transition-colors",
											activeUseMono
												? "font-mono text-[11px] font-bold uppercase tracking-widest"
												: "text-sm font-medium tracking-tight",
											"text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
											"border-[var(--card-border)]",
										)}
									>
										{title}
									</button>
								</TooltipTrigger>
								<TooltipContent>
									<p
										className={cn(
											"max-w-xs text-xs",
											activeUseMono && "font-mono tracking-wide",
										)}
									>
										{subtitle}
									</p>
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					) : (
						<h3
							className={cn(
								"leading-none",
								activeUseMono
									? "font-mono text-[11px] font-bold uppercase tracking-widest"
									: "text-sm font-medium tracking-tight",
								"text-[var(--text-secondary)]",
							)}
						>
							{title}
						</h3>
					)}
				</div>
			</div>
			{/* Action slot */}
			{/* max-w-full, not shrink-0: an action that wraps (the Ratings card's two toggles) wraps here instead of clipping off a phone's edge. */}
			{action && !isLoading && <div className="ml-auto max-w-full">{action}</div>}
		</div>
	);
}

// Scroll-gate sign-in overlay shown on the home page for logged-out users
function ScrollGateOverlay({
	isScrollLocked,
	activeHasGlow,
}: {
	isScrollLocked: boolean;
	activeHasGlow: boolean;
}) {
	return (
		<LazyMotion features={domAnimation}>
			<AnimatePresence>
				{isScrollLocked && (
					<m.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.22, ease: "easeOut" }}
						className="absolute inset-0 z-20 flex items-center justify-center p-4"
					>
						<m.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
							className="absolute inset-0 bg-gradient-to-b from-background/75 to-background/95 backdrop-blur-[3px]"
						/>
						<m.div
							initial={{ opacity: 0, scale: 0.92 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.96 }}
							transition={{ duration: 0.35, ease: "easeOut" }}
							className="absolute inset-x-[12%] top-6 h-24 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.16),transparent_68%)] blur-3xl"
						/>

						<m.div
							initial={{ opacity: 0, y: 18, scale: 0.95 }}
							animate={{ opacity: 1, y: 0, scale: 1 }}
							exit={{ opacity: 0, y: 12, scale: 0.98 }}
							transition={{
								type: "spring",
								stiffness: 340,
								damping: 28,
								mass: 0.9,
							}}
							className={cn(
								"relative w-full max-w-[372px] overflow-hidden rounded-[26px] border p-6 text-left shadow-[0_24px_80px_rgba(0,8,7,0.52)]",
								activeHasGlow
									? "border-[rgb(var(--ast-brand-green-rgb)/0.35)] bg-[linear-gradient(160deg,rgba(var(--ast-brand-night-rgb)/0.96),rgba(7,21,13,0.92),rgba(var(--ast-brand-moss-rgb)/0.96))]"
									: "border-[rgb(var(--ast-brand-forest-rgb)/0.45)] bg-[linear-gradient(160deg,rgba(var(--ast-brand-night-rgb)/0.96),rgba(7,21,13,0.92),rgba(var(--ast-brand-moss-rgb)/0.96))]",
							)}
						>
							<div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),transparent_38%)]" />
							<div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-[rgb(var(--ast-brand-green-rgb)/0.6)] to-transparent" />
							<div className="pointer-events-none absolute -right-10 top-0 h-32 w-32 rounded-full bg-[rgb(var(--ast-brand-green-rgb)/0.12)] blur-3xl" />

							<div className="relative space-y-5">
								<m.div
									initial={{ opacity: 0, y: 10 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0, y: 6 }}
									transition={{ delay: 0.08, duration: 0.22, ease: "easeOut" }}
									className="flex items-center gap-4"
								>
									<div className="inline-flex shrink-0 rounded-2xl border border-[rgb(var(--ast-brand-forest-rgb)/0.45)] bg-white/[0.04] p-3">
										<Image
											src="/favicon/apple-touch-icon.png"
											alt="App Store Tracker"
											width={36}
											height={36}
											className="h-9 w-9 rounded-lg object-cover"
										/>
									</div>
									<div className="min-w-0 text-lg font-semibold tracking-tight text-[var(--ast-brand-text)]">
										Unlock the power. For free.
									</div>
								</m.div>

								<m.div
									initial={{ opacity: 0, y: 10 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0, y: 6 }}
									transition={{ delay: 0.14, duration: 0.22, ease: "easeOut" }}
								>
									<Button
										onClick={openLoginDialog}
										className="h-12 w-full rounded-2xl bg-[var(--ast-brand-green)] text-[var(--ast-brand-night)] shadow-[0_10px_30px_rgba(60,255,122,0.18)] hover:bg-[#52ff8d]"
									>
										<User className="mr-2 h-4 w-4" />
										Sign in
										<ArrowRight className="ml-2 h-4 w-4" />
									</Button>
								</m.div>
							</div>
						</m.div>
					</m.div>
				)}
			</AnimatePresence>
		</LazyMotion>
	);
}

export function ChartCard({
	title,
	subtitle,
	action,
	isLoading = false,
	loadingSkeleton,
	terminalCode,
	children,
	className,
	...props
}: ChartCardProps) {
	const { hasScanlines, hasGlow, hasCRT, useMono, config, mounted } =
		useThemeConfig();
	// Guard all terminal-conditional rendering behind `mounted` to avoid hydration
	// mismatches: server renders with isTerminalMode=false, client reads localStorage after mount.
	const activeHasScanlines = mounted && hasScanlines;
	const activeHasGlow = mounted && hasGlow;
	const activeHasCRT = mounted && hasCRT;
	const activeUseMono = mounted && useMono;
	const hasDramaticAnimations =
		mounted && config.effects.animations === "dramatic";
	const pathname = useRoutePathname();
	const { user } = useAuth();
	const [isScrollLocked, setIsScrollLocked] = useState(false);
	const shouldGateOnScroll = pathname === "/" && !user && !isLoading;
	// Derive the gate from auth so it clears the moment the user signs in —
	// isScrollLocked stays true in state after login (no remount), and the
	// overlay/blur must not linger for an authenticated user.
	const gateActive = isScrollLocked && !user;

	const handleScrollGate = () => {
		if (!shouldGateOnScroll || isScrollLocked) return;
		setIsScrollLocked(true);
	};

	return (
		<div
			className={cn(
				"relative overflow-hidden transition-all duration-300",
				// Background - uses CSS variable token
				"bg-[var(--card-bg)]",
				// Border - uses CSS variable token
				"border border-[var(--card-border)]",
				// Shadow - uses CSS variable token
				"[box-shadow:var(--card-shadow)]",
				// Border radius - no rounding for terminal, rounded for others
				activeHasScanlines ? "rounded-none" : "rounded-sm",
				// Glow effects - border pulse animation for dramatic themes
				activeHasGlow &&
					hasDramaticAnimations &&
					"animate-terminal-border-pulse",
				// Hover state for glow themes
				activeHasGlow && "hover:border-[var(--card-hover-border)]",
				className,
			)}
			onWheelCapture={handleScrollGate}
			onTouchMoveCapture={handleScrollGate}
			{...props}
		>
			<ChartCardDecorations
				activeHasScanlines={activeHasScanlines}
				activeHasGlow={activeHasGlow}
				activeHasCRT={activeHasCRT}
			/>

			{/* Terminal Panel Header - Bloomberg window chrome (only for terminal/glow themes) */}
			{activeHasGlow && (
				<TerminalPanelHeader
					code={terminalCode}
					title={title}
					status="live"
					className="relative z-10"
				/>
			)}

			{/* Content */}
			<div className={cn("relative z-10", activeHasGlow ? "p-3" : "p-2")}>
				{/* Header - simplified for terminal mode since TerminalPanelHeader handles title/status */}
				<ChartCardHeader
					title={title}
					subtitle={subtitle}
					action={action}
					isLoading={isLoading}
					activeHasGlow={activeHasGlow}
					activeUseMono={activeUseMono}
				/>

				{/* Main content */}
				{isLoading ? (
					loadingSkeleton ||
					(activeHasGlow ? (
						<EffectsLoadingState />
					) : (
						<div className="h-64 flex items-center justify-center">
							<Skeleton className="h-full w-full rounded-md" />
						</div>
					))
				) : (
					<div
						className={cn(
							"transition-[filter,opacity,transform] duration-300 ease-out",
							gateActive &&
								"pointer-events-none scale-[0.985] blur-[4px] opacity-15",
						)}
					>
						{children}
					</div>
				)}
			</div>

			<ScrollGateOverlay
				isScrollLocked={gateActive}
				activeHasGlow={activeHasGlow}
			/>
		</div>
	);
}
